import { Component, ElementRef, Input, OnInit, ViewChild } from "@angular/core";
import { TableSummaryCalculatorFunction } from "@app/directives/_layout/v-table/_components/visualisations/table-summary-box/table-summary-box.component";
import { VTableRowStatus } from "@app/directives/_layout/v-table/_enums/VTableRowStatus.enum";
import { IVTableColumn } from "@app/directives/_layout/v-table/_models/IVTableColumn";
import { IVTableRow } from "@app/directives/_layout/v-table/_models/IVTableRow";
import { FallbackCellType, VTableService } from "@app/directives/_layout/v-table/_services/v-table.service";
import { SortDirection } from "@app/enum/SortDirection";
import { SortType } from "@app/enum/SortType";
import { ChartService } from "@app/services/charts.service";
import { DateService, DateUnit } from "@app/services/date.service";
import { FaultCodesService } from "../../fault-codes.service";
import { IDiagnosticRecord } from "../../_models/IDiagnosticRecord";
import "./fault-code-vehicle-history.component.scss";

declare const Highcharts: any;

@Component({
    selector: 'fault-code-vehicle-history',
    template: require("./fault-code-vehicle-history.component.html")
})
export class FaultCodeVehicleHistoryComponent implements OnInit {

    @Input() vehicleId: number;

    @ViewChild("chartContainer", { read: ElementRef }) chartContainer: ElementRef<HTMLElement>;

    // --

    tableColumns: IVTableColumn<IDiagnosticRecord>[] = [];
    tableRows: IVTableRow<IDiagnosticRecord>[] = [];

    diagnosticRecords: IDiagnosticRecord[] = [];
    topFaultCodes: { code: string, count: number }[] = [];
    loading: boolean;

    constructor(
        private readonly vTableService: VTableService,
        private readonly faultCodesService: FaultCodesService,
        private readonly chartService: ChartService,
        private readonly dateService: DateService
    ) {
    }

    ngOnInit(): void {
        this.createTableColumns();
    }

    ngOnChanges(): void {
        this.loadDiagnosticRecords();
    }

    faultCodeCalculator: TableSummaryCalculatorFunction<IDiagnosticRecord, { code: string, count: number }> = (item, allItems, model) => {
        return item.FriendlyCode == model.code ? 1 : 0;
    }

    // ---

    private createTableColumns(): void {
        this.tableColumns = [
            {
                header: this.vTableService.createStatusTableHeader({ sorted: true }),
                createCellFunction: (record) => this.vTableService.createStatusCell(record.DateCleared ? VTableRowStatus.Success : VTableRowStatus.Warning)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "FaultCodes.Code", sortType: SortType.String, sortable: true, width: 100 }),
                createCellFunction: (data) => this.vTableService.createTableCell(data.FriendlyCode)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "FaultCodes.Description", sortType: SortType.String, sortable: true }),
                createCellFunction: (data) => this.vTableService.createTableCell(data.description, {}, FallbackCellType.NA)
            },
            {
                header: this.vTableService.createDateTableHeader({ tKey: "FaultCodes.Raised", sortable: true, sortDirection: SortDirection.Descending, sortType: SortType.Date }),
                createCellFunction: (data) => this.vTableService.createDateCell(data.DateReceived)
            },
            {
                header: this.vTableService.createDateTableHeader({ tKey: "FaultCodes.Cleared", sortable: true, sortType: SortType.Date }),
                createCellFunction: (data) => this.vTableService.createDateCell(data.DateCleared)
            }
        ]
    }

    private loadDiagnosticRecords(): void {
        this.loading = true;
        this.faultCodesService.getVehicleDiagnosticRecords(this.vehicleId)
            .then(records => {
                this.diagnosticRecords = records;
            })
            .finally(() => {
                this.tableRows = this.vTableService.createTableRows(this.diagnosticRecords, this.tableColumns);
                this.loading = false;

                this.topFaultCodes = this.getTopFaultCodes(this.diagnosticRecords, 3);
            });
    }

    private createChart(): void {
        // collate data
        var faultCounts = this.countFaultCodes(this.diagnosticRecords);
        
        const categories: string[] = Object.keys(faultCounts);

        const xRangeSeries: any[] = [];
        const seriesData: any[] = [];

        this.diagnosticRecords.forEach(r => {

            var startDate = new Date(r.DateReceived);
            var endDate = r.DateCleared ? new Date(r.DateCleared) : new Date;
            var minDate = this.dateService.addOrSubtractTime(new Date, -3, DateUnit.Month);

            if (endDate < minDate) {
                return;
            }

            seriesData.push({
                x: startDate.valueOf(),
                x2: endDate.valueOf(),
                y: categories.indexOf(r.DiagnosticTroubleCode),
                name: r.FriendlyCode || r.DiagnosticTroubleCode,
                type: "datetime"
            });
        });
        
        xRangeSeries.push({
            name: "",
            type: 'xrange',
            data: seriesData,            
        });

        // Setup chart
        const chartConfig = this.chartService.createBasicXRangeChart(xRangeSeries);
        chartConfig.exporting = { enabled: false };
        chartConfig.tooltip = { enabled: true, crosshairs: true, followPointer: true };

        const yAxis = chartConfig.yAxis as Highcharts.AxisOptions;
        yAxis.categories = Object.keys(faultCounts);
        yAxis.minPadding = 100;

        this.chartService.useVigetColours(chartConfig);

        // Create Chart
        var chartObject = Highcharts.chart(this.chartContainer.nativeElement, chartConfig);
    }

    private countFaultCodes(records: IDiagnosticRecord[]): Record<string, number> {
        return records            
            .reduce((acc, record) => {
                const fullCode = record.FriendlyCode; // Ensure full code (SPN-FMI) is used
                acc[fullCode] = (acc[fullCode] || 0) + 1;
                return acc;
            }, {} as Record<string, number>);
    }

    private getTopFaultCodes(records: IDiagnosticRecord[], topX: number): { code: string, count: number }[] {
        const faultCounts = this.countFaultCodes(records);

        return Object.entries(faultCounts)
            .sort(([, countA], [, countB]) => countB - countA) // Sort by count (descending)
            .slice(0, topX) // Take only the top X
            .map(([code, count]) => ({ code, count })); // Convert to array of objects
    }
}
